import type { CartLine } from "@/types/order";

/**
 * Cart money, in grosze (integer minor units) end to end. The only division is
 * inside `formatGrosze`.
 *
 * Pricing rule, mirrored from the backend's order-calculations service and from
 * how the bill already reads (`generateReceipt.ts`, `BillsPage.tsx`):
 * a chosen size's price REPLACES the dish's base price (it is absolute, not a
 * delta), and topping prices are ADDED on top.
 */

/**
 * Total price of a set of toppings, honouring BOTH the per-topping quantity the
 * server sends (OrderToppingDto.quantity) and the duplicate `count` groupDishes
 * computes when the same topping appears twice in one dish. Either may be
 * absent, in which case it counts once.
 */
export const toppingsTotalGrosze = (
  toppings: { price: number; quantity?: number; count?: number }[] | undefined,
): number =>
  toppings?.reduce(
    (sum, topping) =>
      sum + topping.price * (topping.quantity ?? 1) * (topping.count ?? 1),
    0,
  ) ?? 0;

export const lineUnitPriceGrosze = (line: CartLine): number =>
  (line.size?.price ?? line.basePrice) +
  line.toppings.reduce(
    (sum, topping) => sum + topping.price * topping.quantity,
    0,
  );

export const lineTotalGrosze = (line: CartLine): number =>
  lineUnitPriceGrosze(line) * line.quantity;

export const cartSubtotalGrosze = (lines: CartLine[]): number =>
  lines.reduce((sum, line) => sum + lineTotalGrosze(line), 0);

export const cartItemCount = (lines: CartLine[]): number =>
  lines.reduce((sum, line) => sum + line.quantity, 0);

export const tipFromPercentGrosze = (
  subtotalGrosze: number,
  percent: number,
): number => Math.round((subtotalGrosze * percent) / 100);

/**
 * The venue's fee on a tip, mirroring the backend's calculateFeeMinorUnits
 * (`Math.round(amount * bps / 10000)`) so the two agree to the grosz.
 *
 * Charged ON TOP of the tip: the person named gets exactly what the guest
 * chose, and the guest pays this as well. The rate comes from the venue's
 * branding as `tipFeeBps` and is usually 0.
 *
 * The bill's own fee (`billFeeBps`) is deliberately not here. It is taken out
 * of what the venue is paid, never added to what the guest is charged, so it
 * has no line on any guest screen.
 */
export const tipFeeGrosze = (
  tipGrosze: number,
  tipFeeBps: number,
): number => Math.round((tipGrosze * tipFeeBps) / 10000);

export const formatGrosze = (grosze: number): string =>
  `${(grosze / 100).toFixed(2)} zł`;

/**
 * Only needed when handing an ordering total to the older bill-splitting
 * drawers, which keep `selectedAmount`/`tipAmount` in major units.
 */
export const groszeToMajor = (grosze: number): number => grosze / 100;
